// SkipJobTypesPage — /skip/settings/job-types
//
// Two-panel layout mirroring SkipStockTypesPage:
//   - LEFT: list of job types (Delivery Only / Scrap Collection / …
//     globals + custom). Globals carry "Default" badge and an
//     activate/deactivate toggle. is_child_only globals carry a
//     "System" badge and remain read-only. Custom types get inline
//     edit + delete + an add form at the bottom.
//   - RIGHT: detail panel for the selected type, three stacked sub-
//     cards: Details (read-only for globals), Stages (ordered list,
//     editable for custom), Actions (verbs with from/to stage,
//     dual_submit, spawns_child).
//
// Read-only enforcement: every mutation control is hidden on globals.
// The server enforces 403 on global mutate; the UI guards proactively.

import { useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { Button } from '../../../components/ui/Button/Button';
import { Card } from '../../../components/ui/Card/Card';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import {
  useActivateSkipJobType,
  useCreateSkipJobType,
  useCreateSkipJobTypeAction,
  useCreateSkipJobTypeStage,
  useDeactivateSkipJobType,
  useDeleteSkipJobType,
  useDeleteSkipJobTypeAction,
  useDeleteSkipJobTypeStage,
  useSkipJobTypeActions,
  useSkipJobTypeDetail,
  useSkipJobTypeStages,
  useSkipJobTypes,
  useUpdateSkipJobType,
  useUpdateSkipJobTypeAction,
  useUpdateSkipJobTypeStage,
} from '../hooks/useSkipJobTypes';
import type {
  SkipJobType,
  SkipJobTypeAction,
  SkipJobTypeStage,
} from '../../../types/api';

import styles from './SkipJobTypesPage.module.css';

// ============================================================ TYPE LIST CARD

interface TypeCardProps {
  jobType: SkipJobType;
  selected: boolean;
  onSelect: () => void;
}

function TypeCard({ jobType, selected, onSelect }: TypeCardProps): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [optimisticActivated, setOptimisticActivated] = useState<boolean | null>(null);

  const updateMutation = useUpdateSkipJobType(jobType.enc_id);
  const deleteMutation = useDeleteSkipJobType(jobType.enc_id);
  const activateMutation = useActivateSkipJobType();
  const deactivateMutation = useDeactivateSkipJobType();

  const isGlobal = jobType.is_global;
  const isChildOnly = jobType.is_child_only;
  const serverActivated = jobType.activated === true;
  const effectiveActivated = optimisticActivated ?? serverActivated;
  const isDeactivatedGlobal = isGlobal && !effectiveActivated;

  function handleToggleActive() {
    setError(null);
    updateMutation.mutate(
      { is_active: !jobType.is_active },
      { onError: (err) => setError(err.message ?? 'Could not save') },
    );
  }

  function handleDelete() {
    setError(null);
    if (!window.confirm(`Delete job type "${jobType.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate(undefined, {
      onError: (err) => setError(err.message ?? 'Could not delete'),
    });
  }

  function handleToggleActivated() {
    setError(null);
    const next = !effectiveActivated;
    setOptimisticActivated(next);
    const mutation = next ? activateMutation : deactivateMutation;
    mutation.mutate(
      { encId: jobType.enc_id },
      {
        onSuccess: () => setOptimisticActivated(null),
        onError: (err) => {
          setOptimisticActivated(null);
          setError(
            err.message ?? (next ? 'Could not activate' : 'Could not deactivate'),
          );
        },
      },
    );
  }

  return (
    <div
      className={[
        styles.typeCard,
        selected ? styles.typeCardActive : '',
        isDeactivatedGlobal ? styles.typeCardDeactivated : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect();
      }}
    >
      <div className={styles.typeCardHeader}>
        <span className={styles.typeName}>
          {jobType.name}
          {isGlobal ? <span className={styles.defaultBadge}>Default</span> : null}
          {isChildOnly ? <span className={styles.systemBadge}>System</span> : null}
          {jobType.process_immediately ? (
            <span className={styles.immediateBadge}>Immediate</span>
          ) : null}
          <span
            className={[styles.prefixBadge, isGlobal ? styles.prefixBadgeMuted : '']
              .filter(Boolean)
              .join(' ')}
          >
            [{jobType.prefix}]
          </span>
        </span>
        {jobType.is_active ? null : <span className={styles.muted}>Inactive</span>}
      </div>
      {jobType.description ? (
        <div className={styles.typeDescription}>{jobType.description}</div>
      ) : null}
      <div className={styles.typeMeta}>
        {jobType.stages_count} stage{jobType.stages_count === 1 ? '' : 's'} ·
        {' '}{jobType.actions_count} action{jobType.actions_count === 1 ? '' : 's'}
      </div>

      {/* Globals: activate/deactivate toggle, no edit/delete. */}
      {isGlobal ? (
        <div className={styles.actionRow} onClick={(e) => e.stopPropagation()}>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={effectiveActivated}
              onChange={handleToggleActivated}
              disabled={activateMutation.isPending || deactivateMutation.isPending}
              aria-label={`Activated ${jobType.name}`}
            />
            {effectiveActivated ? 'Activated' : 'Not activated'}
          </label>
        </div>
      ) : (
        <div className={styles.actionRow} onClick={(e) => e.stopPropagation()}>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={jobType.is_active}
              onChange={handleToggleActive}
              aria-label={`Active ${jobType.name}`}
            />
            Active
          </label>
          <span className={styles.spacer} />
          <Button
            variant="danger"
            icon={Trash2}
            onClick={handleDelete}
            loading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </div>
      )}
      {error ? <p className={styles.error}>{error}</p> : null}
    </div>
  );
}

// ============================================================ ADD TYPE FORM

interface NewTypeForm {
  key: string;
  name: string;
  prefix: string;
  description: string;
  process_immediately: boolean;
  allows_priority: boolean;
}

const EMPTY_NEW_TYPE: NewTypeForm = {
  key: '',
  name: '',
  prefix: '',
  description: '',
  process_immediately: false,
  allows_priority: true,
};

interface AddTypeFormProps {
  existingPrefixes: string[];
  existingKeys: string[];
}

function AddTypeForm({ existingPrefixes, existingKeys }: AddTypeFormProps): JSX.Element {
  const [form, setForm] = useState<NewTypeForm>(EMPTY_NEW_TYPE);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateSkipJobType();

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.prefix.trim() || !form.key.trim()) {
      setError('Key, name and prefix are required.');
      return;
    }
    if (existingPrefixes.includes(form.prefix.toUpperCase())) {
      setError(`Prefix "${form.prefix.toUpperCase()}" is already in use.`);
      return;
    }
    if (existingKeys.includes(form.key.toLowerCase())) {
      setError(`Key "${form.key.toLowerCase()}" is already in use.`);
      return;
    }
    if (!/^[A-Z0-9]+$/.test(form.prefix)) {
      setError('Prefix must be uppercase letters/digits only.');
      return;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(form.key)) {
      setError('Key must be snake_case (lowercase, letters/digits/underscore, first char a letter).');
      return;
    }
    create.mutate(
      {
        key: form.key.toLowerCase(),
        name: form.name.trim(),
        prefix: form.prefix,
        description: form.description.trim() || null,
        process_immediately: form.process_immediately,
        allows_priority: form.allows_priority,
      },
      {
        onSuccess: () => setForm(EMPTY_NEW_TYPE),
        onError: (err) => setError(err.message ?? 'Could not create job type'),
      },
    );
  }

  return (
    <form className={styles.addForm} onSubmit={handleSubmit} aria-label="Add Job Type">
      <label>
        <span>Key</span>
        <input
          required
          value={form.key}
          onChange={(e) =>
            setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })
          }
          maxLength={50}
          placeholder="e.g. exchange_express"
          aria-label="Key"
        />
      </label>
      <label>
        <span>Prefix</span>
        <input
          required
          value={form.prefix}
          onChange={(e) =>
            setForm({ ...form, prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })
          }
          maxLength={10}
          placeholder="e.g. EXX"
          aria-label="Prefix"
        />
      </label>
      <label className={styles.addFormFull}>
        <span>Name</span>
        <input
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          maxLength={100}
          placeholder="e.g. Express Exchange"
        />
      </label>
      <label className={styles.addFormFull}>
        <span>Description (optional)</span>
        <input
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          maxLength={500}
        />
      </label>
      <label className={styles.toggle}>
        <input
          type="checkbox"
          checked={form.process_immediately}
          onChange={(e) =>
            setForm({ ...form, process_immediately: e.target.checked })
          }
          aria-label="Process immediately"
        />
        Process immediately
      </label>
      <label className={styles.toggle}>
        <input
          type="checkbox"
          checked={form.allows_priority}
          onChange={(e) =>
            setForm({ ...form, allows_priority: e.target.checked })
          }
          aria-label="Allows priority"
        />
        Allows priority
      </label>
      {error ? (
        <p className={[styles.error, styles.addFormFull].join(' ')}>{error}</p>
      ) : null}
      <div className={styles.addFormActions}>
        <Button type="submit" variant="primary" icon={Plus} loading={create.isPending}>
          Add Job Type
        </Button>
      </div>
    </form>
  );
}

// ============================================================ DETAILS CARD

interface DetailsCardProps {
  jobType: SkipJobType;
}

function DetailsCard({ jobType }: DetailsCardProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(jobType.name);
  const [description, setDescription] = useState(jobType.description ?? '');
  const [processImmediately, setProcessImmediately] = useState(
    jobType.process_immediately,
  );
  const [allowsPriority, setAllowsPriority] = useState(jobType.allows_priority);
  const [isActive, setIsActive] = useState(jobType.is_active);
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateSkipJobType(jobType.enc_id);

  // Reset form on type change.
  useEffect(() => {
    setEditing(false);
    setName(jobType.name);
    setDescription(jobType.description ?? '');
    setProcessImmediately(jobType.process_immediately);
    setAllowsPriority(jobType.allows_priority);
    setIsActive(jobType.is_active);
    setError(null);
  }, [jobType.enc_id, jobType.name, jobType.description, jobType.process_immediately, jobType.allows_priority, jobType.is_active]);

  function handleSave() {
    setError(null);
    updateMutation.mutate(
      {
        name,
        description: description || null,
        process_immediately: processImmediately,
        allows_priority: allowsPriority,
        is_active: isActive,
      },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => setError(err.message ?? 'Could not save'),
      },
    );
  }

  if (jobType.is_global) {
    return (
      <div className={styles.detailReadonly}>
        <div className={styles.detailReadonlyRow}>
          <span className={styles.detailReadonlyLabel}>Name</span>
          <span className={styles.detailReadonlyValue}>{jobType.name}</span>
        </div>
        <div className={styles.detailReadonlyRow}>
          <span className={styles.detailReadonlyLabel}>Key</span>
          <span className={styles.codeBadge}>{jobType.key}</span>
        </div>
        <div className={styles.detailReadonlyRow}>
          <span className={styles.detailReadonlyLabel}>Prefix</span>
          <span className={styles.codeBadge}>{jobType.prefix}</span>
        </div>
        <div className={styles.detailReadonlyRow}>
          <span className={styles.detailReadonlyLabel}>Description</span>
          <span className={styles.detailReadonlyValue}>
            {jobType.description ?? <em>(none)</em>}
          </span>
        </div>
        <div className={styles.detailReadonlyRow}>
          <span className={styles.detailReadonlyLabel}>Processing</span>
          <span
            className={
              jobType.process_immediately
                ? styles.immediateBadge
                : styles.scheduledBadge
            }
          >
            {jobType.process_immediately ? 'Immediate' : 'Scheduled'}
          </span>
        </div>
        <div className={styles.detailReadonlyRow}>
          <span className={styles.detailReadonlyLabel}>Allows Priority</span>
          <span className={styles.detailReadonlyValue}>
            {jobType.allows_priority ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
    );
  }

  if (!editing) {
    return (
      <div>
        <div className={styles.detailReadonly}>
          <div className={styles.detailReadonlyRow}>
            <span className={styles.detailReadonlyLabel}>Name</span>
            <span className={styles.detailReadonlyValue}>{jobType.name}</span>
          </div>
          <div className={styles.detailReadonlyRow}>
            <span className={styles.detailReadonlyLabel}>Key</span>
            <span className={styles.codeBadge}>{jobType.key}</span>
          </div>
          <div className={styles.detailReadonlyRow}>
            <span className={styles.detailReadonlyLabel}>Prefix</span>
            <span className={styles.codeBadge}>{jobType.prefix}</span>
          </div>
          <div className={styles.detailReadonlyRow}>
            <span className={styles.detailReadonlyLabel}>Description</span>
            <span className={styles.detailReadonlyValue}>
              {jobType.description ?? <em>(none)</em>}
            </span>
          </div>
          <div className={styles.detailReadonlyRow}>
            <span className={styles.detailReadonlyLabel}>Processing</span>
            <span
              className={
                jobType.process_immediately
                  ? styles.immediateBadge
                  : styles.scheduledBadge
              }
            >
              {jobType.process_immediately ? 'Immediate' : 'Scheduled'}
            </span>
          </div>
          <div className={styles.detailReadonlyRow}>
            <span className={styles.detailReadonlyLabel}>Allows Priority</span>
            <span className={styles.detailReadonlyValue}>
              {jobType.allows_priority ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
        <div className={styles.actionRow}>
          <Button variant="ghost" icon={Pencil} onClick={() => setEditing(true)}>
            Edit Details
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.detailFields}>
      <label className={styles.fullSpan}>
        <span>Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
        />
      </label>
      <label className={styles.fullSpan}>
        <span>Description</span>
        <textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
        />
      </label>
      <label className={styles.toggle}>
        <input
          type="checkbox"
          checked={processImmediately}
          onChange={(e) => setProcessImmediately(e.target.checked)}
          aria-label="Process immediately"
        />
        Process immediately
      </label>
      <label className={styles.toggle}>
        <input
          type="checkbox"
          checked={allowsPriority}
          onChange={(e) => setAllowsPriority(e.target.checked)}
          aria-label="Allows priority"
        />
        Allows priority
      </label>
      <label className={[styles.toggle, styles.fullSpan].join(' ')}>
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          aria-label="Is active"
        />
        Active
      </label>
      {error ? <p className={[styles.error, styles.fullSpan].join(' ')}>{error}</p> : null}
      <div className={[styles.actionRow, styles.fullSpan].join(' ')}>
        <Button
          variant="primary"
          icon={Save}
          onClick={handleSave}
          loading={updateMutation.isPending}
        >
          Save
        </Button>
        <Button variant="ghost" icon={X} onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ============================================================ STAGE ROW

/**
 * Inline 6-dot grip icon. Rendered as an SVG so we don't pull in an
 * extra icon library. Cursor + colours come from the parent CSS.
 */
function GripIcon(): JSX.Element {
  return (
    <svg
      width="12"
      height="16"
      viewBox="0 0 12 16"
      aria-hidden
      focusable="false"
    >
      <circle cx="3" cy="3" r="1.4" fill="currentColor" />
      <circle cx="9" cy="3" r="1.4" fill="currentColor" />
      <circle cx="3" cy="8" r="1.4" fill="currentColor" />
      <circle cx="9" cy="8" r="1.4" fill="currentColor" />
      <circle cx="3" cy="13" r="1.4" fill="currentColor" />
      <circle cx="9" cy="13" r="1.4" fill="currentColor" />
    </svg>
  );
}

interface StageRowProps {
  typeEncId: string;
  stage: SkipJobTypeStage;
  parentIsGlobal: boolean;
}

function StageRow({ typeEncId, stage, parentIsGlobal }: StageRowProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(stage.label);
  const [isStart, setIsStart] = useState(stage.is_start);
  const [isTerminal, setIsTerminal] = useState(stage.is_terminal);
  const [triggersDispatch, setTriggersDispatch] = useState(stage.triggers_dispatch);
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateSkipJobTypeStage(typeEncId, stage.enc_id);
  const deleteMutation = useDeleteSkipJobTypeStage(typeEncId, stage.enc_id);

  const isGlobal = stage.is_global || parentIsGlobal;

  // Drag-to-reorder is wired only for custom (non-global) types.
  // useSortable still has to be called unconditionally to satisfy the
  // rules of hooks; the listeners + grip are rendered conditionally.
  const sortable = useSortable({ id: stage.enc_id, disabled: isGlobal });
  const sortableStyle = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };

  function handleSave() {
    setError(null);
    updateMutation.mutate(
      {
        label,
        is_start: isStart,
        is_terminal: isTerminal,
        triggers_dispatch: triggersDispatch,
      },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => setError(err.message ?? 'Could not save'),
      },
    );
  }

  function handleDelete() {
    setError(null);
    if (!window.confirm(`Delete stage "${stage.label}"?`)) return;
    deleteMutation.mutate(undefined, {
      onError: (err) => setError(err.message ?? 'Could not delete'),
    });
  }

  return (
    <div
      ref={sortable.setNodeRef}
      style={sortableStyle}
      className={[
        styles.row,
        sortable.isDragging ? styles.rowDragging : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {isGlobal ? null : (
        <button
          type="button"
          ref={sortable.setActivatorNodeRef}
          className={styles.grip}
          aria-label={`Drag to reorder ${stage.label}`}
          {...sortable.attributes}
          {...sortable.listeners}
        >
          <GripIcon />
        </button>
      )}
      {editing && !isGlobal ? (
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem' }}>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            aria-label="Stage label"
            placeholder="Label"
          />
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={isStart}
              onChange={(e) => setIsStart(e.target.checked)}
            />
            Start
          </label>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={isTerminal}
              onChange={(e) => setIsTerminal(e.target.checked)}
            />
            Terminal
          </label>
          <label className={[styles.toggle, styles.fullSpan].join(' ')}>
            <input
              type="checkbox"
              checked={triggersDispatch}
              onChange={(e) => setTriggersDispatch(e.target.checked)}
            />
            Triggers dispatch
          </label>
          {error ? <p className={styles.error}>{error}</p> : null}
        </div>
      ) : (
        <div style={{ flex: 1 }}>
          <div className={styles.rowName}>
            <span style={{ fontFamily: 'SF Mono, Consolas, monospace', fontSize: '0.78rem', color: 'var(--color-text-tertiary)' }}>
              #{stage.stage_order}
            </span>
            {stage.label}
            <span className={styles.codeBadge}>{stage.key}</span>
            {stage.is_start ? <span className={styles.startBadge}>Start</span> : null}
            {stage.is_terminal ? <span className={styles.terminalBadge}>Terminal</span> : null}
            {stage.triggers_dispatch ? (
              <span className={styles.dispatchBadge}>Dispatch</span>
            ) : null}
          </div>
          {error ? <p className={styles.error}>{error}</p> : null}
        </div>
      )}
      {isGlobal ? null : (
        <div className={styles.rowControls}>
          {editing ? (
            <>
              <Button
                variant="primary"
                icon={Save}
                onClick={handleSave}
                loading={updateMutation.isPending}
              >
                Save
              </Button>
              <Button variant="ghost" icon={X} onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" icon={Pencil} onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button
                variant="danger"
                icon={Trash2}
                onClick={handleDelete}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================ STAGES SUB-CARD

interface StagesListProps {
  typeEncId: string;
  stages: ReadonlyArray<SkipJobTypeStage>;
  parentIsGlobal: boolean;
  isLoading: boolean;
}

/**
 * Stages list inside the detail panel. For custom (non-global) types
 * the list is wrapped in DndContext so rows can be dragged into a new
 * order. On drop we recalculate stage_order 1..N and fire PATCH for
 * every changed stage. While a PATCH is in flight the sub-card shows
 * a loading state; on error the sub-card surfaces the message and
 * the visual order reverts to the last server-confirmed state.
 */
function StagesList({
  typeEncId,
  stages,
  parentIsGlobal,
  isLoading,
}: StagesListProps): JSX.Element {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  // Local view of the current order — pre-mutation drag preview lives
  // here; on success we let react-query refetch and replace the list.
  const [localStages, setLocalStages] = useState<ReadonlyArray<SkipJobTypeStage>>(stages);
  const [pendingCount, setPendingCount] = useState(0);
  const [reorderError, setReorderError] = useState<string | null>(null);

  // Keep local view in sync when the server data refreshes (post-
  // success refetch, switching selected job type, etc.). We
  // intentionally do not mirror local edits back into this — the
  // moment the server replies the list rebuilds from props.
  useEffect(() => {
    setLocalStages(stages);
  }, [stages]);

  const updateForReorder = useReorderStageMutation(typeEncId);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over === null || active.id === over.id) return;
    if (parentIsGlobal) return;
    const fromIdx = localStages.findIndex((s) => s.enc_id === active.id);
    const toIdx = localStages.findIndex((s) => s.enc_id === over.id);
    if (fromIdx === -1 || toIdx === -1) return;

    const previous = localStages;
    const reordered = arrayMove([...localStages], fromIdx, toIdx);
    // Recalculate stage_order 1..N across the whole list. Only the
    // rows whose order actually changed need a PATCH.
    const targets = reordered.map((s, i) => ({ stage: s, newOrder: i + 1 }));
    const changed = targets.filter(({ stage, newOrder }) => stage.stage_order !== newOrder);
    if (changed.length === 0) return;

    // Apply the new orders to the local view immediately for a
    // smooth UX. If any PATCH fails we revert to `previous`.
    const optimistic = reordered.map((s, i) => ({ ...s, stage_order: i + 1 }));
    setLocalStages(optimistic);
    setReorderError(null);
    setPendingCount((c) => c + changed.length);

    let failed = false;
    for (const { stage, newOrder } of changed) {
      updateForReorder.mutate(
        { stageEncId: stage.enc_id, stage_order: newOrder },
        {
          onError: (err) => {
            failed = true;
            setReorderError(err.message ?? 'Could not reorder stages');
            setLocalStages(previous);
          },
          onSettled: () => {
            setPendingCount((c) => c - 1);
            if (!failed) {
              // success — react-query invalidation in the mutation
              // will repopulate `stages` via the parent prop.
            }
          },
        },
      );
    }
  }

  if (isLoading) return <p className={styles.empty}>Loading stages…</p>;
  if (localStages.length === 0) return <p className={styles.empty}>No stages yet.</p>;

  const isReordering = pendingCount > 0;

  if (parentIsGlobal) {
    // Globals are read-only — render rows without DnD wrapping.
    return (
      <div>
        {localStages.map((s) => (
          <StageRow
            key={s.enc_id}
            typeEncId={typeEncId}
            stage={s}
            parentIsGlobal
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={isReordering ? styles.stagesListLoading : undefined}
      aria-busy={isReordering}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={localStages.map((s) => s.enc_id)}
          strategy={verticalListSortingStrategy}
        >
          <div>
            {localStages.map((s) => (
              <StageRow
                key={s.enc_id}
                typeEncId={typeEncId}
                stage={s}
                parentIsGlobal={false}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {reorderError ? <p className={styles.error}>{reorderError}</p> : null}
    </div>
  );
}

/**
 * Single mutation that accepts `{ stageEncId, stage_order }` per
 * call. The standard `useUpdateSkipJobTypeStage(typeEncId, stageEncId)`
 * hook keys by stageEncId at hook-construction time, which would mean
 * one hook instance per row — awkward to drive from a drag-end loop.
 * This wrapper keeps a single mutation function and accepts the
 * stageEncId in the variables.
 */
function useReorderStageMutation(typeEncId: string) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { stageEncId: string; stage_order: number }
  >({
    mutationFn: async ({ stageEncId, stage_order }) => {
      const res = await apiClient.patch<unknown, { stage_order: number }>(
        endpoints.skipJobTypes.stage(typeEncId, stageEncId),
        { stage_order },
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.stages(typeEncId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.detail(typeEncId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
    },
  });
}

interface AddStageFormProps {
  typeEncId: string;
  nextOrder: number;
}

function AddStageForm({ typeEncId, nextOrder }: AddStageFormProps): JSX.Element {
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [isStart, setIsStart] = useState(false);
  const [isTerminal, setIsTerminal] = useState(false);
  const [triggersDispatch, setTriggersDispatch] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateSkipJobTypeStage(typeEncId);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!key.trim() || !label.trim()) {
      setError('Key and label are required.');
      return;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
      setError('Stage key must be snake_case.');
      return;
    }
    // stage_order auto-appends — drag-to-reorder owns explicit
    // ordering, so the add form takes the next slot at the end.
    create.mutate(
      {
        key: key.toLowerCase(),
        label: label.trim(),
        stage_order: nextOrder,
        is_start: isStart,
        is_terminal: isTerminal,
        triggers_dispatch: triggersDispatch,
      },
      {
        onSuccess: () => {
          setKey('');
          setLabel('');
          setIsStart(false);
          setIsTerminal(false);
          setTriggersDispatch(false);
        },
        onError: (err) => setError(err.message ?? 'Could not create stage'),
      },
    );
  }

  return (
    <form className={styles.addForm} onSubmit={handleSubmit} aria-label="Add Stage">
      <label>
        <span>Key</span>
        <input
          required
          value={key}
          onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
          maxLength={50}
          placeholder="e.g. inspecting"
        />
      </label>
      <label className={styles.addFormFull}>
        <span>Label</span>
        <input
          required
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={100}
          placeholder="e.g. Inspecting"
        />
      </label>
      <label className={styles.toggle}>
        <input
          type="checkbox"
          checked={isStart}
          onChange={(e) => setIsStart(e.target.checked)}
        />
        Start
      </label>
      <label className={styles.toggle}>
        <input
          type="checkbox"
          checked={isTerminal}
          onChange={(e) => setIsTerminal(e.target.checked)}
        />
        Terminal
      </label>
      <label className={[styles.toggle, styles.addFormFull].join(' ')}>
        <input
          type="checkbox"
          checked={triggersDispatch}
          onChange={(e) => setTriggersDispatch(e.target.checked)}
        />
        Triggers dispatch
      </label>
      {error ? (
        <p className={[styles.error, styles.addFormFull].join(' ')}>{error}</p>
      ) : null}
      <div className={styles.addFormActions}>
        <Button type="submit" variant="primary" icon={Plus} loading={create.isPending}>
          Add Stage
        </Button>
      </div>
    </form>
  );
}

// ============================================================ ACTION ROW

interface ActionRowProps {
  typeEncId: string;
  action: SkipJobTypeAction;
  parentIsGlobal: boolean;
  stages: ReadonlyArray<SkipJobTypeStage>;
  jobTypes: ReadonlyArray<SkipJobType>;
}

function ActionRow({
  typeEncId,
  action,
  parentIsGlobal,
  stages,
  jobTypes,
}: ActionRowProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [buttonLabel, setButtonLabel] = useState(action.button_label);
  const [modalTitle, setModalTitle] = useState(action.modal_title ?? '');
  const [modalBody, setModalBody] = useState(action.modal_body ?? '');
  const [fromStageUuid, setFromStageUuid] = useState<string | null>(action.from_stage_uuid);
  const [toStageUuid, setToStageUuid] = useState<string | null>(action.to_stage_uuid);
  const [spawnsChildTypeUuid, setSpawnsChildTypeUuid] = useState<string | null>(
    action.spawns_child_type_uuid,
  );
  const [dualSubmit, setDualSubmit] = useState(action.dual_submit);
  const [isPrimary, setIsPrimary] = useState(action.is_primary);
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateSkipJobTypeAction(typeEncId, action.enc_id);
  const deleteMutation = useDeleteSkipJobTypeAction(typeEncId, action.enc_id);

  const isGlobal = action.is_global || parentIsGlobal;

  function handleSave() {
    setError(null);
    updateMutation.mutate(
      {
        button_label: buttonLabel,
        modal_title: modalTitle || null,
        modal_body: modalBody || null,
        from_stage_uuid: fromStageUuid,
        to_stage_uuid: toStageUuid,
        spawns_child_type_uuid: spawnsChildTypeUuid,
        dual_submit: dualSubmit,
        is_primary: isPrimary,
      },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => setError(err.message ?? 'Could not save'),
      },
    );
  }

  function handleDelete() {
    setError(null);
    if (!window.confirm(`Delete action "${action.action_key}"?`)) return;
    deleteMutation.mutate(undefined, {
      onError: (err) => setError(err.message ?? 'Could not delete'),
    });
  }

  const fromStageLabel = action.from_stage_uuid
    ? stages.find((s) => s.uuid === action.from_stage_uuid)?.label ?? '(stage)'
    : 'Any stage';
  const toStageLabel = action.to_stage_uuid
    ? stages.find((s) => s.uuid === action.to_stage_uuid)?.label ?? '(stage)'
    : 'No change';
  const spawnsChildLabel = action.spawns_child_type_uuid
    ? jobTypes.find((t) => t.uuid === action.spawns_child_type_uuid)?.name ?? '(type)'
    : null;

  return (
    <div className={styles.row}>
      {editing && !isGlobal ? (
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <label className={styles.fullSpan}>
            <span>Button label</span>
            <input
              value={buttonLabel}
              onChange={(e) => setButtonLabel(e.target.value)}
              maxLength={100}
            />
          </label>
          <label>
            <span>From stage</span>
            <select
              value={fromStageUuid ?? ''}
              onChange={(e) =>
                setFromStageUuid(e.target.value === '' ? null : e.target.value)
              }
            >
              <option value="">Any stage</option>
              {stages.map((s) => (
                <option key={s.uuid} value={s.uuid}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>To stage</span>
            <select
              value={toStageUuid ?? ''}
              onChange={(e) =>
                setToStageUuid(e.target.value === '' ? null : e.target.value)
              }
            >
              <option value="">No change</option>
              {stages.map((s) => (
                <option key={s.uuid} value={s.uuid}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Spawns child type</span>
            <select
              value={spawnsChildTypeUuid ?? ''}
              onChange={(e) =>
                setSpawnsChildTypeUuid(e.target.value === '' ? null : e.target.value)
              }
            >
              <option value="">None</option>
              {jobTypes.map((t) => (
                <option key={t.uuid} value={t.uuid}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={dualSubmit}
              onChange={(e) => setDualSubmit(e.target.checked)}
            />
            Dual submit
          </label>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
            />
            Primary
          </label>
          <label className={styles.fullSpan}>
            <span>Modal title</span>
            <input
              value={modalTitle}
              onChange={(e) => setModalTitle(e.target.value)}
              maxLength={150}
            />
          </label>
          <label className={styles.fullSpan}>
            <span>Modal body</span>
            <textarea
              rows={2}
              value={modalBody}
              onChange={(e) => setModalBody(e.target.value)}
            />
          </label>
          {error ? <p className={styles.error}>{error}</p> : null}
        </div>
      ) : (
        <div>
          <div className={styles.rowName}>
            <span className={styles.codeBadge}>{action.action_key}</span>
            {action.button_label}
            {action.dual_submit ? (
              <span className={styles.dualSubmitBadge}>Dual Submit</span>
            ) : null}
            {spawnsChildLabel ? (
              <span className={styles.spawnsChildBadge}>
                Spawns: {spawnsChildLabel}
              </span>
            ) : null}
          </div>
          <div className={styles.rowMeta}>
            From: {fromStageLabel} → To: {toStageLabel}
          </div>
          {error ? <p className={styles.error}>{error}</p> : null}
        </div>
      )}
      {isGlobal ? null : (
        <div className={styles.rowControls}>
          {editing ? (
            <>
              <Button
                variant="primary"
                icon={Save}
                onClick={handleSave}
                loading={updateMutation.isPending}
              >
                Save
              </Button>
              <Button variant="ghost" icon={X} onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" icon={Pencil} onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button
                variant="danger"
                icon={Trash2}
                onClick={handleDelete}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface AddActionFormProps {
  typeEncId: string;
  stages: ReadonlyArray<SkipJobTypeStage>;
  jobTypes: ReadonlyArray<SkipJobType>;
}

function AddActionForm({ typeEncId, stages, jobTypes }: AddActionFormProps): JSX.Element {
  const [actionKey, setActionKey] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');
  const [fromStageUuid, setFromStageUuid] = useState<string | null>(null);
  const [toStageUuid, setToStageUuid] = useState<string | null>(null);
  const [spawnsChildTypeUuid, setSpawnsChildTypeUuid] = useState<string | null>(null);
  const [dualSubmit, setDualSubmit] = useState(false);
  const [isPrimary, setIsPrimary] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateSkipJobTypeAction(typeEncId);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!actionKey.trim() || !buttonLabel.trim()) {
      setError('Action key and button label are required.');
      return;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(actionKey)) {
      setError('Action key must be snake_case.');
      return;
    }
    create.mutate(
      {
        action_key: actionKey.toLowerCase(),
        button_label: buttonLabel.trim(),
        from_stage_uuid: fromStageUuid,
        to_stage_uuid: toStageUuid,
        spawns_child_type_uuid: spawnsChildTypeUuid,
        dual_submit: dualSubmit,
        is_primary: isPrimary,
      },
      {
        onSuccess: () => {
          setActionKey('');
          setButtonLabel('');
          setFromStageUuid(null);
          setToStageUuid(null);
          setSpawnsChildTypeUuid(null);
          setDualSubmit(false);
          setIsPrimary(false);
        },
        onError: (err) => setError(err.message ?? 'Could not create action'),
      },
    );
  }

  return (
    <form className={styles.addForm} onSubmit={handleSubmit} aria-label="Add Action">
      <label>
        <span>Action key</span>
        <input
          required
          value={actionKey}
          onChange={(e) => setActionKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
          maxLength={50}
          placeholder="e.g. mark_complete"
        />
      </label>
      <label>
        <span>Button label</span>
        <input
          required
          value={buttonLabel}
          onChange={(e) => setButtonLabel(e.target.value)}
          maxLength={100}
          placeholder="e.g. Mark Complete"
        />
      </label>
      <label>
        <span>From stage</span>
        <select
          value={fromStageUuid ?? ''}
          onChange={(e) =>
            setFromStageUuid(e.target.value === '' ? null : e.target.value)
          }
        >
          <option value="">Any stage</option>
          {stages.map((s) => (
            <option key={s.uuid} value={s.uuid}>
              {s.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>To stage</span>
        <select
          value={toStageUuid ?? ''}
          onChange={(e) =>
            setToStageUuid(e.target.value === '' ? null : e.target.value)
          }
        >
          <option value="">No change</option>
          {stages.map((s) => (
            <option key={s.uuid} value={s.uuid}>
              {s.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>Spawns child type</span>
        <select
          value={spawnsChildTypeUuid ?? ''}
          onChange={(e) =>
            setSpawnsChildTypeUuid(e.target.value === '' ? null : e.target.value)
          }
        >
          <option value="">None</option>
          {jobTypes.map((t) => (
            <option key={t.uuid} value={t.uuid}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <label className={styles.toggle}>
        <input
          type="checkbox"
          checked={dualSubmit}
          onChange={(e) => setDualSubmit(e.target.checked)}
        />
        Dual submit
      </label>
      <label className={[styles.toggle, styles.addFormFull].join(' ')}>
        <input
          type="checkbox"
          checked={isPrimary}
          onChange={(e) => setIsPrimary(e.target.checked)}
        />
        Primary
      </label>
      {error ? (
        <p className={[styles.error, styles.addFormFull].join(' ')}>{error}</p>
      ) : null}
      <div className={styles.addFormActions}>
        <Button type="submit" variant="primary" icon={Plus} loading={create.isPending}>
          Add Action
        </Button>
      </div>
    </form>
  );
}

// ============================================================ PAGE

export function SkipJobTypesPage(): JSX.Element {
  const navigate = useNavigate();
  const typesQuery = useSkipJobTypes();
  const [selectedTypeEncId, setSelectedTypeEncId] = useState<string | null>(null);

  const types = typesQuery.data?.jobTypes ?? [];
  const selectedType = types.find((t) => t.enc_id === selectedTypeEncId) ?? null;

  // Auto-select the first active type once data lands.
  useEffect(() => {
    if (selectedTypeEncId === null && types.length > 0) {
      setSelectedTypeEncId(types[0]!.enc_id);
    }
  }, [types, selectedTypeEncId]);

  const stagesQuery = useSkipJobTypeStages(selectedTypeEncId);
  const actionsQuery = useSkipJobTypeActions(selectedTypeEncId);
  const detailQuery = useSkipJobTypeDetail(selectedTypeEncId);

  const stages = stagesQuery.data?.stages ?? [];
  const actions = actionsQuery.data?.actions ?? [];
  // Refresh the type from the detail endpoint so edits round-trip
  // immediately even if the list cache hasn't ticked yet.
  const detailType = detailQuery.data?.jobType ?? null;
  const effectiveSelected = detailType ?? selectedType;

  const existingPrefixes = types.map((t) => t.prefix.toUpperCase());
  const existingKeys = types.map((t) => t.key.toLowerCase());

  const nextStageOrder =
    stages.length === 0 ? 1 : Math.max(...stages.map((s) => s.stage_order)) + 1;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Job Types"
        subtitle="Manage skip job types, their stage workflows and per-stage actions"
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/skip/dashboard')}>
            Back
          </Button>
        }
      />

      <div className={styles.grid}>
        <Card>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>Job Types</span>
          </div>
          {typesQuery.isLoading ? (
            <p className={styles.empty}>Loading…</p>
          ) : types.length === 0 ? (
            <p className={styles.empty}>No job types yet — add your first below.</p>
          ) : (
            <div className={styles.cardList}>
              {types.map((t) => (
                <TypeCard
                  key={t.enc_id}
                  jobType={t}
                  selected={t.enc_id === selectedTypeEncId}
                  onSelect={() => setSelectedTypeEncId(t.enc_id)}
                />
              ))}
            </div>
          )}
          <AddTypeForm existingPrefixes={existingPrefixes} existingKeys={existingKeys} />
        </Card>

        <Card>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>
              {effectiveSelected
                ? `${effectiveSelected.name} — Workflow`
                : 'Workflow'}
            </span>
          </div>
          {effectiveSelected !== null && effectiveSelected.is_global ? (
            <p className={styles.globalBanner}>
              This is a default job type. You can add your own job types and
              workflows in addition to these defaults.
            </p>
          ) : null}
          {effectiveSelected === null ? (
            <p className={styles.empty}>Select a job type to manage its workflow.</p>
          ) : (
            <>
              <div className={styles.detailCard}>
                <div className={styles.subCardHeader}>Details</div>
                <DetailsCard jobType={effectiveSelected} />
              </div>

              <div className={styles.detailCard}>
                <div className={styles.subCardHeader}>Stages</div>
                <StagesList
                  typeEncId={effectiveSelected.enc_id}
                  stages={stages}
                  parentIsGlobal={effectiveSelected.is_global}
                  isLoading={stagesQuery.isLoading}
                />
                {!effectiveSelected.is_global ? (
                  <AddStageForm
                    typeEncId={effectiveSelected.enc_id}
                    nextOrder={nextStageOrder}
                  />
                ) : null}
              </div>

              <div className={styles.detailCard}>
                <div className={styles.subCardHeader}>Actions</div>
                {actionsQuery.isLoading ? (
                  <p className={styles.empty}>Loading actions…</p>
                ) : actions.length === 0 ? (
                  <p className={styles.empty}>No actions yet.</p>
                ) : (
                  <div>
                    {actions.map((a) => (
                      <ActionRow
                        key={a.enc_id}
                        typeEncId={effectiveSelected.enc_id}
                        action={a}
                        parentIsGlobal={effectiveSelected.is_global}
                        stages={stages}
                        jobTypes={types}
                      />
                    ))}
                  </div>
                )}
                {!effectiveSelected.is_global ? (
                  <AddActionForm
                    typeEncId={effectiveSelected.enc_id}
                    stages={stages}
                    jobTypes={types}
                  />
                ) : null}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
